import React, { useState } from 'react';
import { Form, Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatDistance } from 'date-fns';
import { getDateFnsLocale } from '../shared/utils';
import { useSaving } from '../contexts/SavingContext';

const TodoItem = ({ todo, onChange, onDelete, onEdit, showSource = false }) => {
  const { t, i18n } = useTranslation();
  const { isSaving } = useSaving();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(todo.text);

  // Format creation date as relative time (e.g., "2 days ago")
  const formattedDate = todo.createdAt 
    ? formatDistance(new Date(todo.createdAt), new Date(), { 
        addSuffix: true,
        locale: getDateFnsLocale(i18n.language)
      }) 
    : t('planDetail.todo.justNow');
    
  // Handle status change with global saving indicator
  const handleStatusChange = () => {
    // Call the actual onChange handler
    onChange({
      ...todo,
      status: todo.status === 'completed' ? 'pending' : 'completed'
    });
    
    // The saving state is managed by the parent component
    // through the SavingContext
  };

  // Add formatDate helper function at the top of the component
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const handleEditClick = () => {
    setEditText(todo.text);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (editText.trim()) {
      onEdit({
        ...todo,
        text: editText.trim()
      });
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText(todo.text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div className="d-flex align-items-start border-bottom py-2">
      <div className="todo-item-checkbox me-2 mt-1">
        <Form.Check 
          type="checkbox"
          checked={todo.status === 'completed'}
          onChange={handleStatusChange}
          disabled={isSaving || isEditing}
          aria-label={`Mark "${todo.text}" as ${todo.status === 'completed' ? 'incomplete' : 'complete'}`}
        />
      </div>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        {isEditing ? (
          <div className="mb-1">
            <Form.Control
              as="textarea"
              rows={2}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
            />
            <div className="mt-1">
              <Button variant="success" size="sm" onClick={handleSaveEdit} className="me-2">
                <i className="bi bi-check"></i> {t('planDetail.todo.save')}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleCancelEdit}>
                <i className="bi bi-x"></i> {t('planDetail.todo.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div 
            className={`${todo.status === 'completed' ? 'text-decoration-line-through text-muted' : ''}`}
            style={{ wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}
          >
            {todo.text}
          </div>
        )}
        <div className="d-flex align-items-center mt-1">
          <small className="text-muted me-2">{t('planDetail.todo.added')} {formattedDate}</small>
          {showSource && todo.source === 'day' && (
            <Badge bg="info" pill className="me-2" style={{ fontSize: '0.7rem' }}>
              {todo.dayNumber ? `${t('planDetail.todo.day')} ${todo.dayNumber}: ${formatDate(todo.dayDate)}` : t('planDetail.todo.day')}
            </Badge>
          )}
          {showSource && todo.source === 'plan' && (
            <Badge bg="primary" pill className="me-2" style={{ fontSize: '0.7rem' }}>{t('planDetail.todo.plan')}</Badge>
          )}
        </div>
      </div>
      {!isEditing && (
        <div className="todo-item-buttons">
          <Button 
            variant="link" 
            className="text-primary p-0 ms-2" 
            onClick={handleEditClick}
            disabled={isSaving}
            aria-label={`Edit todo "${todo.text}"`}
          >
            <i className="bi bi-pencil"></i>
          </Button>
          <Button 
            variant="link" 
            className="text-danger p-0 ms-2" 
            onClick={() => onDelete(todo.id)}
            disabled={isSaving}
            aria-label={`Delete todo "${todo.text}"`}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      )}
    </div>
  );
};

export default TodoItem; 